"use client";

import { useCallback, useEffect, useState } from "react";

let recaptchaLoadPromise: Promise<void> | null = null;

function waitForRecaptchaReady(): Promise<void> {
	return new Promise((resolve) => {
		const ready = window.grecaptcha?.enterprise?.ready;
		if (typeof ready === "function") {
			try {
				ready(() => resolve());
				return;
			} catch {
				resolve();
				return;
			}
		}
		resolve();
	});
}

function loadRecaptchaEnterprise(siteKey: string): Promise<void> {
	if (typeof window === "undefined") return Promise.resolve();
	if (window.grecaptcha?.enterprise) return waitForRecaptchaReady();

	if (recaptchaLoadPromise) return recaptchaLoadPromise;

	recaptchaLoadPromise = new Promise((resolve, reject) => {
		const existingScript = document.querySelector<HTMLScriptElement>(
			'script[src*="recaptcha/enterprise.js"]',
		);

		const onReady = () => {
			void waitForRecaptchaReady().then(resolve);
		};

		if (existingScript) {
			if (window.grecaptcha?.enterprise) {
				onReady();
			} else {
				existingScript.addEventListener("load", onReady, { once: true });
				existingScript.addEventListener(
					"error",
					() => reject(new Error("reCAPTCHA script failed to load")),
					{ once: true },
				);
			}
			return;
		}

		try {
			if (typeof navigator !== "undefined" && !("xr" in navigator)) {
				Object.defineProperty(navigator, "xr", {
					value: null,
					writable: false,
					configurable: true,
				});
			}
		} catch {
			// Some browsers lock navigator. reCAPTCHA can still load without this shim.
		}

		const script = document.createElement("script");
		script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
		script.async = true;
		script.defer = true;
		script.onload = onReady;
		script.onerror = () => reject(new Error("reCAPTCHA script failed to load"));
		document.head.appendChild(script);
	});

	recaptchaLoadPromise = recaptchaLoadPromise.catch((error) => {
		recaptchaLoadPromise = null;
		throw error;
	});

	return recaptchaLoadPromise;
}

/**
 * Safe wrapper for reCAPTCHA Enterprise hook
 * Returns undefined executeRecaptcha if provider is not configured
 * This allows the app to work without reCAPTCHA keys in development
 * 
 * Uses reCAPTCHA Enterprise API directly (grecaptcha.enterprise.execute)
 * instead of the standard reCAPTCHA library
 */
export function useSafeReCaptcha() {
	// Check if site key is configured
	const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
	const hasSiteKey = !!siteKey;
	const [mounted, setMounted] = useState(false);
	
	useEffect(() => {
		setMounted(true);
	}, []);
	
	// Execute reCAPTCHA Enterprise token generation
	const executeRecaptcha = useCallback(async (action: string): Promise<string | undefined> => {
		if (!mounted || !hasSiteKey || !siteKey) {
			return undefined;
		}

		if (typeof window === "undefined") {
			return undefined;
		}

		try {
			await loadRecaptchaEnterprise(siteKey);

			// Execute reCAPTCHA Enterprise with the action
			if (
				!window.grecaptcha?.enterprise?.execute ||
				typeof window.grecaptcha.enterprise.execute !== "function"
			) {
				console.warn("reCAPTCHA Enterprise execute method not available");
				return undefined;
			}

			const token = await window.grecaptcha.enterprise.execute(siteKey, { action });
			return token;
		} catch (error) {
			console.error("reCAPTCHA Enterprise error:", error);
			return undefined;
		}
	}, [mounted, hasSiteKey, siteKey]);

	return { executeRecaptcha: hasSiteKey && mounted ? executeRecaptcha : undefined };
}

